/**
 * Unit Brief Store (Scoped Brief P1)
 *
 * Holds the per-section brief (notes + pinned docs, plus a single document's
 * win themes) for the active artifact, keyed by sectionKey. Hydrated from the
 * outline GET (`unitBriefs`). Notes, pins and themes share ONE
 * optimistic-concurrency version: every successful save returns the latest
 * version and we write it back, so a subsequent save of ANOTHER field carries
 * the current version and avoids a spurious 409.
 *
 * On a 409 (server version moved on — edited elsewhere) we DO NOT overwrite
 * local state; we set `conflictSectionKey` so the UI can prompt a reload.
 *
 * The store holds ONE artifact's briefs and records which (`artifactId`). It
 * outlives route changes, so a save aimed at another artifact must never build
 * on this store's list or version (`saveThemes` refuses), and a save response
 * that lands after the store switched artifact is not merged into it.
 */

import { create } from 'zustand'
import {
  saveBriefNotes,
  saveBriefPins,
  saveBriefThemes,
  ApiRequestError,
  type UnitBriefView,
} from '@/lib/api'

interface UnitBriefState {
  /** The artifact whose briefs these are; null until the first hydrate. */
  artifactId: string | null
  /** Briefs keyed by sectionKey. */
  briefs: Record<string, UnitBriefView>
  /** Section whose last save lost the version race — UI shows "edited elsewhere — reload". */
  conflictSectionKey: string | null
  hydrate: (artifactId: string, briefs: UnitBriefView[]) => void
  get: (sectionKey: string) => UnitBriefView | undefined
  /** updated_at for a unit (or null when no brief row yet) — anchors the stale guard. */
  getUpdatedAt: (sectionKey: string) => string | null
  saveNotes: (artifactId: string, sectionKey: string, notes: string) => Promise<void>
  savePins: (artifactId: string, sectionKey: string, pinnedDocIds: string[]) => Promise<void>
  /**
   * Replace a single document's win themes (only where `themesLiveOnUnitBrief`
   * holds — an outlined section's themes are saved on the outline). Resolves
   * `true` once stored. Never throws: a 409 records `conflictSectionKey` and
   * resolves `false`; any other failure resolves `false` too — the same
   * contract as `useDraftingStore.updateSectionAndSave`, which callers swap it
   * for. Refuses (resolves `false`, writes nothing) unless the store holds
   * `artifactId`'s briefs — the caller built `themeIds` from this store.
   */
  saveThemes: (artifactId: string, sectionKey: string, themeIds: string[]) => Promise<boolean>
}

/** A stale-version save surfaces as HTTP 409 from the api layer. */
function isConflict(err: unknown): boolean {
  return err instanceof ApiRequestError && err.statusCode === 409
}

/** Does the store (still) hold this artifact's briefs? */
function holds(artifactId: string): boolean {
  return useUnitBriefStore.getState().artifactId === artifactId
}

export const useUnitBriefStore = create<UnitBriefState>((set, get) => ({
  artifactId: null,
  briefs: {},
  conflictSectionKey: null,

  hydrate: (artifactId, briefs) =>
    set({
      artifactId,
      briefs: Object.fromEntries(briefs.map((b) => [b.sectionKey, b])),
      conflictSectionKey: null,
    }),

  get: (sectionKey) => get().briefs[sectionKey],

  getUpdatedAt: (sectionKey) => get().briefs[sectionKey]?.updatedAt ?? null,

  saveNotes: async (artifactId, sectionKey, notes) => {
    const current = get().briefs[sectionKey]
    try {
      const { unitBrief } = await saveBriefNotes(
        artifactId,
        sectionKey,
        notes,
        current?.version ?? null,
      )
      // The store may have moved to another artifact while this was in flight.
      if (holds(artifactId)) {
        set((s) => ({
          briefs: { ...s.briefs, [sectionKey]: unitBrief },
          conflictSectionKey: null,
        }))
      }
      // Notes changed → any pending plan for this section is now stale (§4.3a-C3).
      // Dynamic import avoids a circular module dependency with agent-store.
      const { useAgentStore } = await import('@/stores/agent-store')
      useAgentStore.getState().markPlanStaleForSection(sectionKey)
    } catch (err) {
      if (isConflict(err)) {
        if (holds(artifactId)) set({ conflictSectionKey: sectionKey })
        return
      }
      throw err
    }
  },

  savePins: async (artifactId, sectionKey, pinnedDocIds) => {
    const current = get().briefs[sectionKey]
    try {
      const { unitBrief } = await saveBriefPins(
        artifactId,
        sectionKey,
        pinnedDocIds,
        current?.version ?? null,
      )
      // The store may have moved to another artifact while this was in flight.
      if (holds(artifactId)) {
        set((s) => ({
          briefs: { ...s.briefs, [sectionKey]: unitBrief },
          conflictSectionKey: null,
        }))
      }
      // Pins changed → any pending plan for this section is now stale (§4.3a-C3).
      const { useAgentStore } = await import('@/stores/agent-store')
      useAgentStore.getState().markPlanStaleForSection(sectionKey)
    } catch (err) {
      if (isConflict(err)) {
        if (holds(artifactId)) set({ conflictSectionKey: sectionKey })
        return
      }
      throw err
    }
  },

  saveThemes: async (artifactId, sectionKey, themeIds) => {
    // Backstop: `themeIds` and the version below come from this store, so a
    // store holding another artifact would write that artifact's list here.
    if (!holds(artifactId)) return false
    const current = get().briefs[sectionKey]
    let saved: UnitBriefView
    try {
      const response = await saveBriefThemes(artifactId, sectionKey, themeIds, current?.version ?? null)
      saved = response.unitBrief
    } catch (err) {
      if (isConflict(err) && holds(artifactId)) set({ conflictSectionKey: sectionKey })
      return false
    }
    // The write landed. Reflect it only if the store still holds this artifact.
    if (holds(artifactId)) {
      set((s) => ({
        briefs: { ...s.briefs, [sectionKey]: saved },
        conflictSectionKey: null,
      }))
    }
    // Themes changed → any pending plan for this unit is now stale (§4.3a-C3).
    const { useAgentStore } = await import('@/stores/agent-store')
    useAgentStore.getState().markPlanStaleForSection(sectionKey)
    return true
  },
}))
